import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Ban, RotateCcw, Plus, Search } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import { useFlash } from '../../context/ToastContext.jsx';
import AnimatedCounter from '../../components/AnimatedCounter.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';
import { useAdminQueues } from '../../context/AdminQueueContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';

// /admin/users — the account roster as an open grid: search + filter pills up
// top, one row per operator. Deleted accounts stay hidden until the ALL or
// DELETED pill is picked (same default as the old "Show deleted" switch).

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'admins', label: 'Admins' },
  { key: 'deleted', label: 'Deleted' },
];

const COLS = { '--adm-cols': 'minmax(300px, 1.6fr) 150px 190px 190px 190px', '--adm-minw': '1044px' };

export default function UsersList() {
  const { user: me } = useAuth();
  const { accessPending, designPending } = useAdminQueues();
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState('active');
  const [query, setQuery] = useState('');
  const setFlash = useFlash();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  function load() {
    setUsers(null);
    api.adminListUsers()
      .then((data) => setUsers(data?.users ?? []))
      .catch((e) => setFlash({ kind: 'danger', msg: e.message }));
  }

  useEffect(() => { load(); }, []);

  async function softDelete(target) {
    setBusy(true);
    try {
      await api.adminDeleteUser(target.id);
      setFlash({ kind: 'success', msg: `${target.username} was disabled.` });
      load();
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
      setConfirmDelete(null);
    }
  }

  async function restore(target) {
    setBusy(true);
    try {
      await api.adminUpdateUser(target.id, { deletedAt: null });
      setFlash({ kind: 'success', msg: `${target.username} restored.` });
      load();
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  const all = users ?? [];
  const activeCount = all.filter((u) => !u.deletedAt).length;
  const adminCount = all.filter((u) => u.role === 'admin' && !u.deletedAt).length;
  const queueCount = (accessPending ?? 0) + (designPending ?? 0);

  const counts = {
    all: all.length,
    active: activeCount,
    admins: adminCount,
    deleted: all.length - activeCount,
  };

  // No loading early-return: a failed load must still show its error toast,
  // or the error is a silent infinite spinner.
  const visible = useMemo(() => {
    let list = all;
    if (filter === 'active') list = list.filter((u) => !u.deletedAt);
    if (filter === 'deleted') list = list.filter((u) => u.deletedAt);
    if (filter === 'admins') list = list.filter((u) => u.role === 'admin');
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((u) => u.username.toLowerCase().includes(q));
    return list;
  }, [all, filter, query]);

  return (
    <AdminShell crumb="Users" userCount={activeCount || undefined}>
      <header className="adm-head adm-head--split adm-rv" style={{ animationDelay: '80ms' }}>
        <div>
          <h1 className="adm-head__title">Users</h1>
          <p className="adm-head__sub">
            Tournament-organizer accounts, roles, and overlay keys. Deleted accounts are hidden by default.
          </p>
        </div>
        <div className="adm-stats">
          <div className="adm-stat adm-stat--ok">
            <div className="adm-stat__value"><AnimatedCounter to={activeCount} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Active</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--amber">
            <div className="adm-stat__value"><AnimatedCounter to={adminCount} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Admins</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--blue">
            <div className="adm-stat__value"><AnimatedCounter to={queueCount} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Queue</div>
          </div>
        </div>
      </header>


      <div className="adm-filterbar adm-rv" style={{ animationDelay: '140ms' }}>
        <div className="adm-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users…"
            aria-label="Search users"
          />
        </div>
        <div className="adm-pills" role="group" aria-label="Filter users">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`adm-pill${filter === f.key ? ' is-active' : ''}`}
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
            >
              {f.label} <span className="adm-pill__count">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <span className="adm-shown">{visible.length} of {all.length} shown</span>
        <Link to="/admin/users/new" className="adm-act">
          <Plus size={13} aria-hidden="true" />
          <span>New User</span>
        </Link>
      </div>

      {users == null ? (
        <Loader label="Loading users…" />
      ) : (
        <div className="adm-scroll adm-scroll--cards adm-rv" style={{ ...COLS, animationDelay: '220ms' }}>
          <div className="adm-grid__head">
            <span>Operator</span>
            <span>Role</span>
            <span>Created</span>
            <span>Status</span>
            <span className="is-right">Actions</span>
          </div>
          <div className="adm-grid__rule" aria-hidden="true" />
          {visible.map((u, i) => (
            <div
              className="adm-grid__row adm-rvl"
              key={u.id}
              style={{
                animationDelay: `${90 + i * 45}ms`,
                '--row-dim': u.role === 'admin' ? 'rgba(255,45,120,0.07)' : 'rgba(0,212,255,0.06)',
              }}
            >
              <div className="adm-ident">
                <span
                  className={`adm-initial${u.role === 'admin' ? ' adm-initial--pink' : ''}`}
                  aria-hidden="true"
                >
                  {u.username?.[0]?.toUpperCase() ?? '?'}
                </span>
                <span className="adm-ident__meta">
                  <span className="adm-ident__name">{u.username}</span>
                  <span className="adm-ident__id" title={u.id}>{u.id?.slice(0, 8)}</span>
                </span>
              </div>
              {/* display:contents on desktop — these three flow into the row
                  grid as before; on mobile the wrapper becomes the card's
                  chip line under the identity. */}
              <div className="adm-grid__meta">
                <div>
                  <span className={`adm-role adm-role--${u.role === 'admin' ? 'admin' : 'user'}`}>
                    {u.role}
                  </span>
                </div>
                <div className="adm-cell adm-cell--dim" data-mlabel="since">
                  {new Date(u.createdAt).toLocaleDateString()}
                </div>
                <div>
                  {u.deletedAt ? (
                    <span className="adm-chip adm-chip--bad">Deleted</span>
                  ) : (
                    <span className="adm-chip adm-chip--ok adm-chip--pulse">Active</span>
                  )}
                </div>
              </div>
              <div className="adm-grid__actions">
                <Link
                  to={`/admin/users/${u.id}`}
                  className="btn-icon btn-icon--primary"
                  title="Edit user"
                  aria-label="Edit user"
                >
                  <Pencil size={16} aria-hidden="true" />
                </Link>
                {u.deletedAt ? (
                  <button
                    type="button"
                    className="btn-icon btn-icon--success"
                    disabled={busy}
                    onClick={() => restore(u)}
                    title="Restore user"
                    aria-label="Restore user"
                  >
                    <RotateCcw size={16} aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-icon btn-icon--danger"
                    disabled={busy || u.id === me?.id}
                    onClick={() => setConfirmDelete(u)}
                    title={u.id === me?.id ? "Can't disable yourself" : 'Disable user'}
                    aria-label="Disable user"
                  >
                    <Ban size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="adm-empty">
              {query.trim() ? 'No users match — try a different search.' : 'No users to show.'}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title={`Disable ${confirmDelete?.username}?`}
        body={
          <>
            <p className="mb-2">This will:</p>
            <ul className="text-muted mb-0">
              <li>Hide them from listings (filter "Deleted" to see)</li>
              <li>Block any sign-in attempts immediately</li>
              <li>Invalidate their OBS URLs (overlay key still on row but auth blocks)</li>
            </ul>
            <p className="text-muted small mb-0 mt-3">You can restore them later.</p>
          </>
        }
        confirmLabel="Disable"
        confirmKind="danger"
        onConfirm={() => confirmDelete && softDelete(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      />
    </AdminShell>
  );
}
